/**
 * useAzureRealtimeAdapter
 *
 * Implements the `VoiceProvider` interface (Task 1.a) by orchestrating the
 * three existing Azure Realtime hooks:
 *   - `useRealtimeSession`   — WebSocket lifecycle, VAD, transcription, responses
 *   - `useRealtimeAudio`     — PCM16 mic capture via AudioWorklet
 *   - `useRealtimeAudioPlayback` — Gapless TTS playback
 *
 * This adapter does NOT modify the existing hooks — it wraps them and
 * normalises their events into the unified `VoiceProvider` contract.
 *
 * DevPlan 0053 — Tasks 2.a / 2.b / 2.c / 2.d
 */
import { logger } from '@/lib/logger';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useRealtimeSession } from './useRealtimeSession';
import { useRealtimeAudio } from './useRealtimeAudio';
import { useRealtimeAudioPlayback } from './useRealtimeAudioPlayback';
import type {
  VoiceOption,
  VoiceProvider,
  VoiceSessionConfig,
  VoiceStatus,
} from './useVoiceConversation';

// ── Azure voice catalogue (Task 2.b) ─────────────────────────────────────

const AZURE_VOICES: VoiceOption[] = [
  { id: 'alloy', name: 'Alloy', category: 'Neutral', language: 'multilingual' },
  { id: 'echo', name: 'Echo', category: 'Warm', language: 'multilingual' },
  { id: 'shimmer', name: 'Shimmer', category: 'Clear', language: 'multilingual' },
  { id: 'verse', name: 'Verse', category: 'Expressive', language: 'multilingual' },
  { id: 'ballad', name: 'Ballad', category: 'Soft', language: 'multilingual' },
];

// ── Simple event emitter (one-off per instance) ───────────────────────────

type Listener<T extends unknown[]> = (...args: T) => void;

function createEmitter<T extends unknown[]>() {
  const listeners = new Set<Listener<T>>();
  return {
    on(cb: Listener<T>): () => void {
      listeners.add(cb);
      return () => { listeners.delete(cb); };
    },
    emit(...args: T) {
      listeners.forEach((cb) => cb(...args));
    },
    clear() {
      listeners.clear();
    },
  };
}

// ── Hook ──────────────────────────────────────────────────────────────────

export interface UseAzureRealtimeAdapterOptions {
  /**
   * Called after each turn's context-sync completes with extracted entities
   * and relationships. Mirrors the existing `onEntitiesExtracted` callback
   * in the Interview ChatInput (Task 2.d).
   */
  onEntitiesExtracted?: (
    entities: Record<string, unknown>[],
    relationships: Record<string, unknown>[],
  ) => void;

  /** Called when the AI invokes the `end_interview` function tool. */
  onSessionEndRequested?: () => void;
}

/**
 * Azure Realtime adapter that satisfies the `VoiceProvider` interface.
 *
 * Internally wires `useRealtimeSession` + `useRealtimeAudio` +
 * `useRealtimeAudioPlayback` and exposes a single, normalised API.
 */
export function useAzureRealtimeAdapter(
  options: UseAzureRealtimeAdapterOptions = {},
): VoiceProvider {
  // ── Status (Task 2.c) ────────────────────────────────────────────────

  const [status, setStatus] = useState<VoiceStatus>('idle');

  // ── Emitters — stable references across renders ──────────────────────

  const emitters = useRef({
    transcript: createEmitter<[string, boolean]>(),
    response: createEmitter<[string]>(),
    audioChunk: createEmitter<[string]>(),
    statusChange: createEmitter<[VoiceStatus]>(),
    turnComplete: createEmitter<[string, string]>(),
  });

  // ── Config snapshot for the active session ───────────────────────────

  const configRef = useRef<VoiceSessionConfig | null>(null);
  const voiceRef = useRef<string>('alloy');

  // audioOutput as React state so hooks pick up changes on re-render (Task 4.f)
  const [audioOutputState, setAudioOutputState] = useState(false);

  // Helper to update status and notify listeners.
  const updateStatus = useCallback((next: VoiceStatus) => {
    setStatus(next);
    emitters.current.statusChange.emit(next);
  }, []);

  // ── Session callbacks ────────────────────────────────────────────────

  const handleTranscriptUpdate = useCallback((text: string, isFinal = false) => {
    // Azure sends empty string '' when a new turn starts → interim.
    // Non-empty → could be interim or final (we treat transcription.completed as final).
    emitters.current.transcript.emit(text, isFinal);
  }, []);

  const handleResponseToken = useCallback((token: string) => {
    emitters.current.response.emit(token);
  }, []);

  const handleTurnComplete = useCallback(
    (userTranscript: string, aiResponse: string) => {
      emitters.current.turnComplete.emit(userTranscript, aiResponse);
    },
    [],
  );

  const handleError = useCallback(
    (msg: string) => {
      logger.error('AzureRealtimeAdapter', msg);
      updateStatus('idle');
    },
    [updateStatus],
  );

  const handleAudioChunk = useCallback((base64: string) => {
    emitters.current.audioChunk.emit(base64);
  }, []);

  // ── Playback hook (always called for hook-rules) ──────────────────────

  const playback = useRealtimeAudioPlayback({
    enabled: audioOutputState,
  });

  // ── Session hook ──────────────────────────────────────────────────────

  const session = useRealtimeSession({
    sessionId: configRef.current?.sessionId ?? '',
    workspaceId: configRef.current?.workspaceId ?? '',
    topic: configRef.current?.topic,
    templateId: configRef.current?.templateId,
    featureContext: configRef.current?.featureContext,
    pageContext: configRef.current?.pageContext,
    audioOutput: audioOutputState,
    voice: voiceRef.current,
    onTranscriptUpdate: handleTranscriptUpdate,
    onResponseToken: handleResponseToken,
    onTurnComplete: handleTurnComplete,
    onError: handleError,
    onSessionEndRequested: options.onSessionEndRequested,
    onEntitiesExtracted: options.onEntitiesExtracted,
    onAudioChunk: useCallback(
      (base64: string) => {
        handleAudioChunk(base64);
        playback.enqueueChunk(base64);
      },
      [handleAudioChunk, playback],
    ),
  });

  // ── Audio capture hook ────────────────────────────────────────────────

  const audio = useRealtimeAudio({
    onAudioChunk: session.sendAudio,
    onLevelChange: undefined,
  });

  // ── Status mapping (Task 2.c) ────────────────────────────────────────
  //
  // Map the three independent boolean flags from useRealtimeSession into
  // a single VoiceStatus enum:
  //   connecting  = not yet connected
  //   listening   = connected + VAD speech_started
  //   thinking    = connected + AI responding + not listening
  //   speaking    = connected + AI responding + audio output
  //   idle        = not connected (default)

  useEffect(() => {
    let next: VoiceStatus;

    if (!session.isConnected) {
      // If we had a config (= connect was called), show connecting.
      next = configRef.current ? 'connecting' : 'idle';
    } else if (session.isListening) {
      next = 'listening';
    } else if (session.isResponding) {
      // When audio output is on and playback is active → 'speaking',
      // otherwise → 'thinking' (text-only response generation).
      next = playback.isPlaying ? 'speaking' : 'thinking';
    } else {
      // Connected but idle between turns — treat as 'listening'
      // (waiting for VAD to fire, mic is still open).
      next = 'listening';
    }

    updateStatus(next);
  }, [
    session.isConnected,
    session.isListening,
    session.isResponding,
    playback.isPlaying,
    updateStatus,
  ]);

  // ── VoiceProvider methods ─────────────────────────────────────────────

  const connect = useCallback(
    async (config: VoiceSessionConfig) => {
      configRef.current = config;
      voiceRef.current = config.voiceId ?? 'alloy';
      setAudioOutputState(config.audioOutput ?? false);
      updateStatus('connecting');

      // Wait one tick so the hooks re-render with the new audioOutput state
      // before we open the WebSocket (which reads audioOutput from its closure).
      await new Promise((r) => setTimeout(r, 0));

      await session.connect(config.token);
      await audio.startCapture();
    },
    [session, audio, updateStatus],
  );

  const disconnect = useCallback(async () => {
    audio.stopCapture();
    session.disconnect();
    playback.stopPlayback();
    configRef.current = null;
    setAudioOutputState(false);
    updateStatus('idle');
  }, [session, audio, playback, updateStatus]);

  const sendAudio = useCallback(
    (chunk: string) => {
      session.sendAudio(chunk);
    },
    [session],
  );

  const setVoice = useCallback(
    (voiceId: string) => {
      voiceRef.current = voiceId;
      if (session.isConnected) {
        session.updateAudioSettings(audioOutputState, voiceId);
      }
    },
    [session, audioOutputState],
  );

  const setAudioOutput = useCallback(
    (enabled: boolean) => {
      setAudioOutputState(enabled);
      if (configRef.current) {
        configRef.current = { ...configRef.current, audioOutput: enabled };
      }
      if (session.isConnected) {
        session.updateAudioSettings(enabled, voiceRef.current);
        if (!enabled) playback.stopPlayback();
      }
    },
    [session, playback],
  );

  const getAvailableVoices = useCallback(async (): Promise<VoiceOption[]> => {
    return AZURE_VOICES;
  }, []);

  // ── Event subscriptions ───────────────────────────────────────────────

  const onTranscript = useCallback(
    (cb: (text: string, isFinal: boolean) => void) =>
      emitters.current.transcript.on(cb),
    [],
  );

  const onResponse = useCallback(
    (cb: (text: string) => void) => emitters.current.response.on(cb),
    [],
  );

  const onAudioChunkSubscribe = useCallback(
    (cb: (base64: string) => void) => emitters.current.audioChunk.on(cb),
    [],
  );

  const onStatusChange = useCallback(
    (cb: (s: VoiceStatus) => void) => emitters.current.statusChange.on(cb),
    [],
  );

  const onTurnComplete = useCallback(
    (cb: (userTranscript: string, aiResponse: string) => void) =>
      emitters.current.turnComplete.on(cb),
    [],
  );

  // ── Cleanup emitters on unmount ───────────────────────────────────────

  useEffect(() => {
    return () => {
      emitters.current.transcript.clear();
      emitters.current.response.clear();
      emitters.current.audioChunk.clear();
      emitters.current.statusChange.clear();
      emitters.current.turnComplete.clear();
    };
  }, []);

  // ── Return VoiceProvider shape ────────────────────────────────────────

  return {
    id: 'azure-realtime',
    status,
    connect,
    disconnect,
    sendAudio,
    setVoice,
    setAudioOutput,
    onTranscript,
    onResponse,
    onAudioChunk: onAudioChunkSubscribe,
    onStatusChange,
    onTurnComplete,
    getAvailableVoices,
  };
}
